import { Injectable, inject } from '@angular/core';
import {
  GraphSetpointData,
  GraphThresholdSetpoint,
  TargetMaxCanopyPostBody,
} from '@cropx-common/models/DeviceGraphDataResponse';
import { SetpointFetchersService } from '@cropx-common/modules/graphs/components/fetcher/fetchers-setpoints';
import { DeviceApiService } from '@cropx-common/services/api-gateway/DeviceApiService';
import {
  Action,
  State,
  StateContext,
  createPropertySelectors,
} from '@ngxs/store';
import dayjs from 'dayjs';
import { produce } from 'immer';
import { Observable } from 'rxjs/internal/Observable';
import { tap } from 'rxjs/operators';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace TargetMaxCanopy {
  const type = 'TargetMaxCanopy';

  export class FetchTargetMaxCanopy {
    static readonly type = `[${type}] FetchTargetMaxCanopy`;
    constructor(public deviceInstallationUuid: string) {}
  }

  export class PostTargetCanopy {
    static readonly type = `[${type}] PostTargetCanopy`;

    constructor(
      public deviceInstallationUuid: string,
      public body: TargetMaxCanopyPostBody,
    ) {}
  }

  export class UpdateTargetCanopy {
    static readonly type = `[${type}] UpdateTargetCanopy`;

    constructor(
      public deviceInstallationUuid: string,
      public setpointUuid: string,

      public body: TargetMaxCanopyPostBody,
    ) {}
  }
}

export interface TargetMaxCanopyStateModel {
  setpoints: GraphSetpointData<GraphThresholdSetpoint>;
  setPointsLoading: boolean;
}

@State<TargetMaxCanopyStateModel>({
  name: 'TargetMaxCanopy',
  defaults: {
    setpoints: null,
    setPointsLoading: false,
  },
})
@Injectable()
export class TargetMaxCanopyState {
  private api = inject(DeviceApiService);
  private setpointFetcher = inject(SetpointFetchersService);

  @Action(TargetMaxCanopy.FetchTargetMaxCanopy)
  fetchTargetMaxCanopy(
    ctx: StateContext<TargetMaxCanopyStateModel>,
    action: TargetMaxCanopy.FetchTargetMaxCanopy,
  ): Observable<any> {
    const { deviceInstallationUuid } = action;

    ctx.patchState({ setPointsLoading: true, setpoints: null });
    const fetcher = this.setpointFetcher.targetMaxCanopySetPoints(
      deviceInstallationUuid,
    );
    const to = dayjs().valueOf();
    const from = dayjs().subtract(1, 'year').valueOf();

    const request = fetcher.fetch(from, to);

    return request.pipe(
      tap((resp: GraphSetpointData<GraphThresholdSetpoint>) => {
        ctx.patchState({ setpoints: resp, setPointsLoading: false });
      }),
    );
  }
  @Action(TargetMaxCanopy.PostTargetCanopy)
  postTargetCanopyTarget(
    ctx: StateContext<TargetMaxCanopyStateModel>,
    action: TargetMaxCanopy.PostTargetCanopy,
  ): Observable<any> {
    const { deviceInstallationUuid, body } = action;

    ctx.patchState({ setPointsLoading: true });
    return this.api.postTargetMaxCanopy(deviceInstallationUuid, body).pipe(
      tap((resp) => {
        ctx.setState(
          produce((state) => {
            const data = state.setpoints.data.filter(
              (sp) => sp.uuid !== resp.uuid,
            );
            data.push(resp);
            data.sort((a, b) => a.timestamp - b.timestamp);
            state.setpoints.data = data;
          }),
        );

        ctx.patchState({ setPointsLoading: false });
      }),
    );
  }
  @Action(TargetMaxCanopy.UpdateTargetCanopy)
  updateTargetCanopy(
    ctx: StateContext<TargetMaxCanopyStateModel>,
    action: TargetMaxCanopy.UpdateTargetCanopy,
  ): Observable<any> {
    const { deviceInstallationUuid, setpointUuid, body } = action;

    ctx.patchState({ setPointsLoading: true });

    return this.api
      .updateTargetMaxCanopy(deviceInstallationUuid, setpointUuid, body)
      .pipe(
        tap((resp) => {
          ctx.setState(
            produce((state) => {
              const data = state.setpoints.data.filter(
                (sp) => sp.uuid !== resp.uuid,
              );
              data.push(resp);
              data.sort((a, b) => a.timestamp - b.timestamp);
              state.setpoints.data = data;
            }),
          );

          ctx.patchState({ setPointsLoading: false });
        }),
      );
  }
}

export class TargetMaxCanopySelectors {
  static slices =
    createPropertySelectors<TargetMaxCanopyStateModel>(TargetMaxCanopyState);
}
